import { Table, StatusBadge } from '@components/ui';
import { Clock, Scale, Paperclip, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCariAccountByCompanyId } from '@mocks/cariHesaplar';
import { resolvePayee } from '@mocks/planliOdemeler';
import clsx from 'clsx';

export default function ScheduledPaymentsTable({ data, onRowClick, onCariClick, activeTab = 'debts' }) {
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getFrequencyLabel = (freq) => {
        const labels = { monthly: 'Aylık', weekly: 'Haftalık', 'one-time': 'Tek Seferlik', 'taksitli': 'Taksitli Borç' };
        return labels[freq] || freq;
    };

    const getCategoryVariant = (category) => {
        const variants = {
            'İşçilik': 'info',
            'Malzeme Alımı': 'warning',
            'Taşeron Hakedişi': 'error',
            'Fatura': 'tertiary',
            'Sigorta': 'success',
            'Tesisat': 'primary',
        };
        return variants[category] || 'default';
    };

    const columns = [
        {
            key: 'description',
            header: activeTab === 'debts' ? 'Tedarikçi / Borç Açıklaması' : 'Sabit Gider Açıklaması',
            width: 'xl',
            render: (val, row) => {
                const payeeName = resolvePayee(row.supplierId) || row.payee || '-';
                const cariAccount = activeTab === 'debts' && row.supplierId?.startsWith('comp-')
                    ? getCariAccountByCompanyId(row.supplierId)
                    : null;
                return (
                    <div className="flex flex-col">
                        <span className="font-semibold text-text-primary">{activeTab === 'debts' ? payeeName : val}</span>
                        <span className="text-xs text-text-muted mt-0.5">{activeTab === 'debts' ? val : row.category}</span>
                        {/* ERP: Çek/Senet Bağlantısı Simülasyonu */}
                        {activeTab === 'debts' && row.status !== 'completed' && row.amount > 50000 && (
                            <span className="text-[10px] text-warning mt-1 flex items-center gap-1 font-medium">
                                <Clock size={10} /> Bekleyen Çek/Senet var
                            </span>
                        )}
                        {/* Cari Hesap Bakiye Bağlantısı */}
                        {cariAccount && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onCariClick?.(cariAccount); }}
                                className={clsx(
                                    'mt-1 flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full w-fit transition-colors',
                                    cariAccount.currentBalance > 0
                                        ? 'bg-error/10 text-error hover:bg-error/20'
                                        : 'bg-success/10 text-success hover:bg-success/20'
                                )}
                            >
                                <Scale size={9} />
                                Cari:{' '}
                                {cariAccount.currentBalance > 0
                                    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(cariAccount.currentBalance)
                                    : 'Kapalı'}
                            </button>
                        )}
                        {/* Malzeme Sipariş Linki */}
                        {row.material_order_id && (
                            <Link
                                to={`/insaat`}
                                onClick={(e) => e.stopPropagation()}
                                className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-primary hover:text-primary/80 transition-colors"
                            >
                                <Package size={9} />
                                Malzeme Siparişi &rarr;
                            </Link>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'category',
            header: 'Kategori',
            width: 'sm',
            align: 'center',
            render: (val) => <StatusBadge status={val} variant={getCategoryVariant(val)} />,
        },
        {
            key: 'amount',
            header: 'Tutar',
            width: 'md',
            align: 'right',
            render: (val, row) => (
                <div className="flex flex-col items-end gap-0.5">
                    <span className="text-error font-medium">{formatCurrency(val)}</span>
                    {row.receiptUrl && (
                        <span className="flex items-center gap-1 text-[10px] text-warning font-medium">
                            <Paperclip size={10} /> Dekont
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'frequency',
            header: 'Sıklık',
            width: 'sm',
            align: 'center',
            render: (val) => getFrequencyLabel(val),
        },
        {
            key: 'nextDueDate',
            header: 'Sonraki Vade',
            width: 'md',
            align: 'center',
            render: (val) => formatDate(val),
        },
        {
            key: 'status',
            header: 'Durum',
            width: 'sm',
            align: 'center',
            render: (val) => (
                <StatusBadge
                    status={val === 'active' ? 'Aktif' : 'Planlandı'}
                    variant={val === 'active' ? 'success' : 'warning'}
                />
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            data={data}
            onRowClick={onRowClick}
            showRowNumbers
            emptyMessage="Planlı ödeme bulunamadı."
            emptyIcon={<Clock size={40} />}
        />

    );
}
